// Timesheet Approvals - dAdmin surface. Lists Submitted timesheets, opens a
// detail modal per row, and lets the reviewer Approve or Reject (reject
// requires a reason). Backend contract mirrors dTime's own /api/timesheet/*
// routes but is served by dAdmin so approvers stay inside dAdmin.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import { dnAlert } from "./utils/dNews_Alert";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./Timesheet_approvals.css";

const DAY_KEYS = ["mon_hours", "tue_hours", "wed_hours", "thu_hours", "fri_hours", "sat_hours", "sun_hours"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Format "2026-07-13" (Monday) → "13 Jul – 19 Jul, 2026" range for the table.
function formatWeekRange(weekStartISO) {
    if (!weekStartISO) return "-";
    const start = new Date(weekStartISO);
    if (Number.isNaN(start.getTime())) return weekStartISO;
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const fmt = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    return `${fmt(start)} - ${fmt(end)}, ${start.getFullYear()}`;
}

function formatDateTime(iso) {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

function rowTotal(row) {
    return DAY_KEYS.reduce((s, k) => s + (parseFloat(row[k]) || 0), 0);
}

// A row is "time-off" when its entry_type marks a day the employee wasn't
// working. dtime.timesheet_entries actually stores this as 'timeoff' - a
// single row that already combines holidays + approved leaves (half-day = 4h).
// 'leave'/'holiday' are kept as aliases for safety in case any legacy row
// carries those values.
function isTimeOffRow(row) {
    const t = String(row.entry_type || "").toLowerCase();
    return t === "timeoff" || t === "leave" || t === "holiday";
}

// Standard workday used to decide "half day means max 4h work".
const STANDARD_DAY = 8;

// Return an array of 7 booleans (Mon..Sun). Index i is true when that day
// has an impossible total per the rule:
//   - partial time-off day (0 < off < 8h)  → work + off must stay <= 8h
//   - normal day (off = 0)                  → only flag > 24h
//   - full-day time off (off >= 8h)         → never flag
function computeDayFlags(detailRows) {
    return DAY_KEYS.map((k) => {
        let total = 0;
        let off = 0;
        for (const r of detailRows) {
            const v = parseFloat(r[k]) || 0;
            total += v;
            if (isTimeOffRow(r)) off += v;
        }
        if (off > 0 && off < STANDARD_DAY) return total > STANDARD_DAY;
        return total > 24;
    });
}

function Timesheet_approvals({ navSize }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRows, setDetailRows] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [target, setTarget] = useState(null); // { emp_id, emp_name, week_start, department_name, submitted_time, total_hours }
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchLogged();
        fetchApprovals();
    }, []);

    const fetchLogged = async () => {
        try {
            const res = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                navigate("/login");
            }
        } catch (e) {
            console.error("logined_employee:", e);
        }
    };

    const fetchApprovals = async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/timesheet/approvals", { credentials: "include" });
            const data = await res.json();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("approvals fetch:", e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const openDetail = async (row) => {
        setTarget(row);
        setDetailOpen(true);
        setDetailRows([]);
        setDetailLoading(true);
        try {
            const params = new URLSearchParams({ emp_id: row.emp_id, week_start: row.week_start });
            const res = await apiFetch(`/api/timesheet/approval_detail?${params.toString()}`, { credentials: "include" });
            const data = await res.json();
            setDetailRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("detail fetch:", e);
            setDetailRows([]);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setDetailOpen(false);
        setTarget(null);
        setDetailRows([]);
        setRejectOpen(false);
        setRejectReason("");
    };

    const submitDecision = async (status, reason = null) => {
        if (!target) return;
        setSubmitting(true);
        try {
            const res = await apiFetch("/api/timesheet/approval_status", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    emp_id: target.emp_id,
                    week_start: target.week_start,
                    status,
                    reason,
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                dnAlert.error("Failed", data?.error || "Could not update timesheet");
                return;
            }
            dnAlert.success("Done", `Timesheet ${status}`);
            closeDetail();
            fetchApprovals();
        } catch (e) {
            console.error("submitDecision:", e);
            dnAlert.error("Failed", "Network error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = () => {
        if (!rejectReason.trim()) {
            dnAlert.warning("Reason required", "Please provide a rejection reason.");
            return;
        }
        submitDecision("Rejected", rejectReason.trim());
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.emp_name || "").toLowerCase().includes(q) ||
            (r.emp_id || "").toLowerCase().includes(q) ||
            (r.department_name || "").toLowerCase().includes(q)
        );
    }, [rows, search]);

    // Which day columns violate the "hours possible on this day" rule.
    // Recomputed only when detail rows change.
    const dayFlags = useMemo(() => computeDayFlags(detailRows), [detailRows]);
    const anyDayOver = dayFlags.some(Boolean);

    // Summary for the modal footer.
    const detailTotals = useMemo(() => {
        let billable = 0, nonBillable = 0;
        detailRows.forEach(r => {
            if (r.entry_type !== "work") return;
            const t = rowTotal(r);
            if ((r.client_billable || "").toLowerCase() === "billable") billable += t;
            else nonBillable += t;
        });
        return {
            billable: Number(billable.toFixed(2)),
            nonBillable: Number(nonBillable.toFixed(2)),
            grand: Number((billable + nonBillable).toFixed(2)),
        };
    }, [detailRows]);

    return (
        <div className="ts-container">
            <div className={`ts-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img
                                src={logo_eagle}
                                alt="dAdmin Logo"
                                style={{ height: 50, objectFit: "contain", marginLeft: -20 }}
                            />
                        </a>
                        Timesheet Approvals
                    </h2>
                    <p className="dspr-subtitle">
                        Review, approve, or reject employee timesheets submitted for the week. Reject requires a reason;
                        an email is sent to the employee on decision.
                    </p>
                </div>

                <div className="ts-toolbar">
                    <input
                        type="text"
                        className="ts-search"
                        placeholder="Search by name, employee ID, or department…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className="ts-count">
                        {loading ? "…" : `${filtered.length} submitted`}
                    </div>
                </div>

                <div className="ts-card">
                    <table className="ts-table">
                        <thead>
                            <tr>
                                <th>Employee</th>
                                <th>Employee ID</th>
                                <th>Department</th>
                                <th>Week</th>
                                <th>Submitted</th>
                                <th>Total Hours</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={8} className="ts-empty">Loading…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={8} className="ts-empty">No submitted timesheets waiting for review.</td></tr>
                            ) : filtered.map(r => (
                                <tr key={`${r.emp_id}-${r.week_start}`}>
                                    <td className="ts-emp-cell">{r.emp_name || "-"}</td>
                                    <td><span className="ts-emp-id">{r.emp_id}</span></td>
                                    <td>{r.department_name || "-"}</td>
                                    <td>{formatWeekRange(r.week_start)}</td>
                                    <td>{formatDateTime(r.submitted_time)}</td>
                                    <td className="ts-hours">{Number(r.total_hours || 0).toFixed(2)}</td>
                                    <td>
                                        <span className="ts-pill ts-pill-submitted">Submitted</span>
                                    </td>
                                    <td>
                                        <button className="ts-btn ts-btn-review" onClick={() => openDetail(r)}>
                                            Review
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail modal */}
            {detailOpen && target && (
                <div className="ts-modal-overlay">
                    <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ts-modal-header">
                            <div>
                                <div className="ts-modal-title">Review timesheet</div>
                                <div className="ts-modal-sub">
                                    {target.emp_name} <span className="ts-emp-id">{target.emp_id}</span>
                                    {target.department_name && <> · {target.department_name}</>}
                                    <> · {formatWeekRange(target.week_start)}</>
                                </div>
                            </div>
                            <button className="ts-modal-close" onClick={closeDetail}>×</button>
                        </div>

                        <div className="ts-modal-body">
                            {anyDayOver && (
                                <div className="ts-warn-banner">
                                    Hours in red exceed the allowed limit: on those days the work hours plus leave/holiday
                                    go over what the day permits (a half-day leave day allows only 4h of work). Please
                                    review before approving.
                                </div>
                            )}
                            {detailLoading ? (
                                <div className="ts-empty">Loading entries…</div>
                            ) : detailRows.length === 0 ? (
                                <div className="ts-empty">No entries for this week.</div>
                            ) : (
                                <div className="ts-detail-scroll">
                                    <table className="ts-detail-table">
                                        <thead>
                                            <tr>
                                                <th>Type</th>
                                                <th>Project</th>
                                                <th>Task</th>
                                                <th>Onsite/Offshore</th>
                                                <th>Billable</th>
                                                <th>Location</th>
                                                {DAY_LABELS.map((d, i) => (
                                                    <th key={d} className={dayFlags[i] ? "ts-over" : ""}>{d}</th>
                                                ))}
                                                <th>Total</th>
                                                <th>Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detailRows.map(row => {
                                                const isOff = isTimeOffRow(row);
                                                return (
                                                    <tr key={row.auto_id}>
                                                        <td>{row.entry_type}</td>
                                                        <td>
                                                            {row.project_id ? (
                                                                <>
                                                                    <div className="ts-proj-name">{row.project_name || row.project_id}</div>
                                                                    <div className="ts-proj-id">{row.project_id}</div>
                                                                </>
                                                            ) : "-"}
                                                        </td>
                                                        <td>{row.task_id || "-"}</td>
                                                        <td>{row.onsite_offshore || "-"}</td>
                                                        <td>{row.client_billable || "-"}</td>
                                                        <td>{row.billing_location || "-"}</td>
                                                        {DAY_KEYS.map((k, i) => {
                                                            // Highlight this cell if the day is over limit AND
                                                            // this row is either a work row or the time-off row -
                                                            // matches the prompt (header + work + timeoff cells).
                                                            const flag = dayFlags[i] && (isOff || String(row.entry_type || "").toLowerCase() === "work");
                                                            return (
                                                                <td key={k} className={`ts-hours${flag ? " ts-over" : ""}`}>
                                                                    {Number(row[k] || 0).toFixed(2)}
                                                                </td>
                                                            );
                                                        })}
                                                        <td className="ts-hours ts-hours-total">
                                                            {rowTotal(row).toFixed(2)}
                                                        </td>
                                                        <td className="ts-comments">{row.comments || "-"}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="ts-modal-footer">
                            <div className="ts-summary">
                                <div><span>Billable</span> <strong>{detailTotals.billable.toFixed(2)}</strong></div>
                                <div><span>Non-Billable</span> <strong>{detailTotals.nonBillable.toFixed(2)}</strong></div>
                                <div><span>Grand Total</span> <strong>{detailTotals.grand.toFixed(2)}</strong></div>
                            </div>
                            <div className="ts-actions">
                                <button
                                    className="ts-btn ts-btn-reject"
                                    onClick={() => setRejectOpen(true)}
                                    disabled={submitting}
                                >
                                    Reject
                                </button>
                                <button
                                    className="ts-btn ts-btn-approve"
                                    onClick={() => submitDecision("Approved")}
                                    disabled={submitting}
                                >
                                    {submitting ? "Saving…" : "Approve"}
                                </button>
                            </div>
                        </div>

                        {rejectOpen && (
                            <div className="ts-reject-panel">
                                <div className="ts-reject-title">Rejection reason</div>
                                <textarea
                                    className="ts-reject-input"
                                    rows={3}
                                    placeholder="Explain what needs correcting so the employee can resubmit."
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                />
                                <div className="ts-reject-actions">
                                    <button className="ts-btn ts-btn-cancel" onClick={() => { setRejectOpen(false); setRejectReason(""); }}>
                                        Cancel
                                    </button>
                                    <button className="ts-btn ts-btn-reject" onClick={handleReject} disabled={submitting}>
                                        {submitting ? "Saving…" : "Confirm Reject"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Timesheet_approvals;
